import { action, makeObservable, observable } from "mobx";
import {
  IPageRequest,
  IPagedResult,
  ISelectOption,
} from "src/Shared/Interfaces";
import { IRootStore } from "src/Shared/Stores/Root.store";
import { ICompany, ICompanyFilter, ICompanyList } from "../Interfaces";
import CompanyService from "../Services/Company.service";
import { Subscription, finalize } from "rxjs";
import { AxiosError } from "axios";
import { ICompanyOverview } from "../Interfaces/ICompanyOverview";
import { IOpenClientContext } from "src/Shared/Layout/Header/Header.utils";
import { IArchivedCompany } from "../Interfaces/IArchivedCompany";
import { ToastService } from "src/Shared/Services";
import Form from "mobx-react-form";
import { getFormValidation } from "src/Shared/Components";
import DateTimeUtils from "../../../Shared/Tools/DateTimeUtils";
import { numericFormatter } from "react-number-format";
import { GridPaginationModel } from "@mui/x-data-grid";
import { AttachmentTypeEnum } from "src/Shared/Enums/AttachmentType.enum";
import { ICompanySLAInfoDTO } from "../Interfaces/ICompanySLAInfoDTO";
import { ICompanyImperansIntegration } from "../Interfaces/ICompanyImperansIntegration";
import { IMenuGroupResponse } from "../Interfaces/IMenuGroupResponse";
import { ICompanyActivity, IActivityPagedResponse } from "../Interfaces/ICompanyActivity";
import { ISLAInfo } from "../Interfaces/ISLAInfo";
import { IProspectList } from "../Interfaces/IProspectList";

/**
 * An unfiltered Home grid. This is a function rather than a shared constant because the collection is
 * handed to an observable field and to the reset below, and a shared object would let one mutate the
 * other's defaults.
 */
const emptyCompanyFilter = (): ICompanyFilter => ({
  companyName: null,
  modifiedBy: { label: "", value: "" },
  franchiseID: [],
  franchiseOfficeID: [],
  totalStaffNumber: null,
  vendorTypeID: null,
  leadTypeIDs: [],
  services: [],
  dealStage: [],
  isSLASigned: null,
  slaExpiryDate: null,
  tddaPrimaryRelationshipManager: [],
  healthTickPrimaryRelationshipManager: [],
  createdBy: [],
  primaryContactFullName: null,
  status: null,
});

export class CompanyStore {
  rootStore: IRootStore;
  public gridLoading: boolean = false;

  public companyList: IPagedResult<ICompanyList> = {
    pageNumber: 0,
    pageSize: 25,
    results: [],
    totalNumberOfRecords: 0,
  };

  public company: ICompany | null = null;
  public archivedCompany: IArchivedCompany | null = null;
  public companyOverview: ICompanyOverview | null = null;

  /**
   * The client the user currently has open, as the app bar needs it (bug 5688).
   *
   * The tab highlight is status-aware, and the client record was the only screen that loaded a
   * status — so the wizard, SOP and setup-copy routes fell back to HOME whenever the record had not
   * been visited first. Every screen that opens a client now publishes this small context from data
   * it already fetches, so one rule serves every client-scoped route however it was reached.
   */
  public openClientContext: IOpenClientContext | null = null;
  public slaInfo: ISLAInfo | null = null;
  public companyRelationshipManager: ISelectOption[] = [];
  public overViewLoading: boolean = false;
  /**
   * True once a client workspace has been asked for and the client turned out not to exist.
   *
   * Distinct from `companyOverview === null`, which is also the legitimate state of the Add New
   * Client route — that ambiguity is what let a hard-deleted client's URL render the Add New Client
   * placeholders as though it were a real, editable record (bug 5823).
   */
  public companyNotFound: boolean = false;
  public companyLoading: boolean = false;
  public pagination: GridPaginationModel = { page: 0, pageSize: 25 };
  public companyFilterForm: Form = null;
  public companyFilterCollection: ICompanyFilter = emptyCompanyFilter();
  public companySlaInfo: ICompanySLAInfoDTO | null = null;
  companyImperansIntegration: ICompanyImperansIntegration;
  menu: IMenuGroupResponse[];
  activities: ICompanyActivity[] = [];
  public activityPagination = { page: 1, pageSize: 10, hasMore: true };
  public activitiesLoading: boolean = false;
  // Live count of all prospect-stage clients (Prospect + Onboarding), ignoring grid filters.
  public prospectsCount: number = 0;
  public prospects: IProspectList[] = [];
  public prospectsLoading: boolean = false;
  // Only-my-clients on the Prospects grid; persisted per user between sessions (P.3, decision 19 Aug 2026).
  public prospectsOnlyMine: boolean = true;

  constructor(rootStore: IRootStore) {
    makeObservable(this, {
      gridLoading: observable,
      companyList: observable,
      company: observable,
      companyOverview: observable,
      openClientContext: observable,
      companyRelationshipManager: observable,
      companySlaInfo: observable,
      setGridLoading: action,
      getCompanies: action.bound,
      setCompanyList: action,
      getCompany: action,
      resetCompanyList: action,
      resetHomeGridState: action,
      getCompanyOverview: action,
      overViewLoading: observable,
      companyNotFound: observable,
      companyLoading: observable,
      archivedCompany: observable,
      getArchivedCompany: action,
      intilizeFilterForm: action,
      companyFilterForm: observable,
      companyFilterCollection: observable,
      setCompanyFilterCollection: action,
      pagination: observable,
      setPagination: action,
      getCompanySlaInfo: action,
      getCompanyImperansIntegration: action,
      companyImperansIntegration: observable,
      getCompanyMenu: action,
      menu: observable,
      activities: observable,
      getCompanyRelationshipManager: action,
      slaInfo: observable,
      getSLAInfo: action,
      resetcompanyOverview: action,
      setOpenClientContext: action,
      clearOpenClientContext: action,
      activitiesLoading: observable,
      activityPagination: observable,
      prospectsCount: observable,
      setProspectsCount: action,
      getProspectsCount: action,
      prospects: observable,
      prospectsLoading: observable,
      prospectsOnlyMine: observable,
      getProspects: action,
      setProspectsOnlyMine: action,
      loadProspectsOnlyMine: action,
    });
    this.rootStore = rootStore;
  }

  setGridLoading = (flag: boolean): void => {
    this.gridLoading = flag;
  };
  getCompanies(request: IPageRequest<ICompanyFilter>): Subscription {
    this.setGridLoading(true);
    return CompanyService.getCompanies(request)
      .pipe(
        finalize(() => {
          this.setGridLoading(false);
        }),
      )
      .subscribe({
        next: (data) => {
          this.setCompanyList(data);
        },
        error: (error: AxiosError) => ToastService.axiosError(error),
      });
  }

  setCompanyList(data: IPagedResult<ICompanyList>) {
    if (data) {
      data.results = data.results.map((x) => {
        x.createdOn = DateTimeUtils.dateToLocalDateZone(x.createdOn);
        x.updatedOn = DateTimeUtils.dateToLocalDateZone(x.updatedOn);
        return x;
      });
      this.companyList = data;
    }
  }

  setCompanyFilterCollection = (filterCollection: ICompanyFilter) => {
    this.companyFilterCollection = filterCollection;
  };

  resetCompanyList = () => {
    const companyList = { ...this.companyList };
    companyList.results = [];
    this.setCompanyList(companyList);
  };

  /**
   * Puts Home back to an unfiltered page one (bug 5722).
   *
   * Home's filter and page number are held here rather than in the grid, so they outlive any
   * navigation away from Home. That is wanted while the user moves around the app, but not when
   * something has just promised to show them a specific row on Home: the row is ordered by Updated
   * On descending and so sits on page one of the unfiltered list, and nowhere else reliably.
   */
  resetHomeGridState = () => {
    this.companyFilterCollection = emptyCompanyFilter();
    this.pagination = { page: 0, pageSize: 25 };
    this.resetCompanyList();
  };

  // showLoader=false loads the record without the app-level full-page loader, so a
  // section of the client workspace can fill itself in without blanking the header
  // and sidebar around it.
  getCompany = (companyRowID: string, showLoader: boolean = true) => {
    if (showLoader) this.rootStore.uiStore.setLoader(true);
    this.companyLoading = true;
    return CompanyService.getCompany(companyRowID)
      .pipe(
        finalize(() => {
          if (showLoader) this.rootStore.uiStore.setLoader(false);
          this.companyLoading = false;
        }),
      )
      .subscribe({
        next: (data) => {
          if (data) {
            data.createdByOn = `${
              data.createdBy
            } - ${DateTimeUtils.dateToLocalTimeZone(data.createdOn)}`;
            data.modifiedByOn = `${
              data.lastModifiedBy
            } - ${DateTimeUtils.dateToLocalTimeZone(data.modifiedOn)}`;

            if (data.importedBy && data.importedOn) {
              data.importedByOn = `${
                data.importedBy
              } - ${DateTimeUtils.dateToLocalTimeZone(data.importedOn)}`;
            }

            // Keep salesTotalValue as a raw number/string so the form can bind it correctly
            data.salesTotalValue = data.salesTotalValue ?? null;
            data.tddaAttachments = data.attachments.filter(
              (x) => x.attachmentTypeID == AttachmentTypeEnum.TDDA,
            );
            data.healthTickAttachments = data.attachments.filter(
              (x) => x.attachmentTypeID == AttachmentTypeEnum.HealthTick,
            );
          }
          this.company = data;
        },
        error: (error: AxiosError) => ToastService.axiosError(error),
      });
  };

  getArchivedCompany = (archivedCRMDataID: number) => {
    this.rootStore.uiStore.setLoader(true);
    return CompanyService.getArchivedCompany(archivedCRMDataID)
      .pipe(
        finalize(() => {
          this.rootStore.uiStore.setLoader(false);
        }),
      )
      .subscribe({
        next: (data) => {
          this.archivedCompany = data;
        },
        error: (error: AxiosError) => ToastService.axiosError(error),
      });
  };

  // silent=true refreshes the overview in the background: no global Backdrop, no
  // overViewLoading flip. Used after a save so only the changed content repaints —
  // the loader belongs to the initial load of the record, not to every edit.
  getCompanyOverview = (companyRowID: string, silent: boolean = false) => {
    // Cleared per request, so a previous miss never condemns the next client that is asked for.
    this.companyNotFound = false;
    if (!silent) {
      this.rootStore.uiStore.setGlobalLoader(true);
      this.overViewLoading = true;
    }
    return CompanyService.getCompanyOverview(companyRowID)
      .pipe(
        finalize(() => {
          if (!silent) {
            this.rootStore.uiStore.setGlobalLoader(false);
            this.overViewLoading = false;
          }
        }),
      )
      .subscribe({
        next: (data) => {
          // The API answers 200 with an empty body for a client that is not there, so this is the
          // only signal of a hard-deleted or never-existent GUID. It used to fall straight through to
          // data.companyRowID below and throw inside next() — which rxjs reports as an unhandled
          // error rather than routing to the error handler, so nothing was shown and the overview was
          // left null. Null then read as "Add New Client" and the shell rendered (bug 5823).
          if (!data) {
            this.companyOverview = null;
            this.companyNotFound = true;
            this.clearOpenClientContext();
            return;
          }
          this.companyOverview = data;
          this.setOpenClientContext({
            companyRowID: data.companyRowID,
            clientStatusID: data.clientStatusID,
          });
          this.companyOverview.slaExpiryDate =
            this.companyOverview.slaExpiryDate != null
              ? DateTimeUtils.dateToLocalDateZone(
                  this.companyOverview.slaExpiryDate,
                )
              : this.companyOverview.slaExpiryDate;

          this.companyOverview.priceReviewDate =
            this.companyOverview.priceReviewDate != null
              ? DateTimeUtils.dateToLocalDateZone(
                  this.companyOverview.priceReviewDate,
                )
              : this.companyOverview.priceReviewDate;
        },
        error: (error: AxiosError) => ToastService.axiosError(error),
      });
  };

  intilizeFilterForm = (fields: {}) => {
    if (this.companyFilterForm === null)
      this.companyFilterForm = getFormValidation(fields);
  };

  setPagination = (data: GridPaginationModel) => {
    this.pagination = { ...data };
  };

  getCompanySlaInfo = (companyRowID: string) => {
    return CompanyService.getCompanySLAInfo(companyRowID)
      .pipe(
        finalize(() => {
          // this.rootStore.uiStore.setGlobalLoader(false);
          // this.overViewLoading = false;
        }),
      )
      .subscribe({
        next: (data) => {
          data.slaExpiryDate =
            data.slaExpiryDate != null
              ? DateTimeUtils.dateToLocalDateZone(data.slaExpiryDate)
              : data.slaExpiryDate;
          this.companySlaInfo = data;
        },
        error: (error: AxiosError) => ToastService.axiosError(error),
      });
  };

  getCompanyImperansIntegration = (companyRowID: string) => {
    return CompanyService.getCompanyImperansIntegration(companyRowID)
      .pipe(
        finalize(() => {
          // this.rootStore.uiStore.setLoader(false);
        }),
      )
      .subscribe({
        next: (data) => {
          this.companyImperansIntegration = data;
        },
        error: (error: AxiosError) => ToastService.axiosError(error),
      });
  };

  // showLoader=false refreshes the sidebar count badges silently. The default
  // loader is the app-level full-page GlobalLoader — background refreshes after a
  // save must not blank the whole page, only the initial workspace load may.
  getCompanyMenu = (companyRowID?: string, showLoader: boolean = true) => {
    if (showLoader) this.rootStore.uiStore.setLoader(true);

    return CompanyService.getCompanyMenu(companyRowID)
      .pipe(
        finalize(() => {
          if (showLoader) this.rootStore.uiStore.setLoader(false);
        }),
      )
      .subscribe({
        next: (data) => {
          this.menu = data;
        },
        error: (error: AxiosError) => ToastService.axiosError(error),
      });
  };

  getCompanyActitvity = (companyRowID: string, isLoadMore: boolean = false) => {
    if (!isLoadMore) {
      this.activityPagination.page = 1;
      this.activityPagination.hasMore = true;
      this.activities = [];
    }

    if (!this.activityPagination.hasMore) return;

    this.activitiesLoading = true;
    return CompanyService.getCompanyActivity(
      companyRowID,
      this.activityPagination.page,
      this.activityPagination.pageSize,
    )
      .pipe(
        finalize(() => {
          this.activitiesLoading = false;
        }),
      )
      .subscribe({
        next: (data) => {
          const formatted = data.activities.map((x) => {
            x.modifiedOn = DateTimeUtils.dateToLocalTimeZone(x.modifiedOn);
            return x;
          });

          if (isLoadMore) {
            this.activities = [...this.activities, ...formatted];
          } else {
            this.activities = formatted;
          }

          this.activityPagination.hasMore = data.hasMore;
          if (data.hasMore) {
            this.activityPagination.page++;
          }
        },
        error: (error: AxiosError) => ToastService.axiosError(error),
      });
  };

  savePriceReviewComment = (request: any) => {
    return CompanyService.savePriceReviewComment(request);
  };

  getCompanyRelationshipManager = (companyRowID: string) => {
    this.rootStore.uiStore.setLoader(true);

    return CompanyService.getCompanyRelationshipManager(companyRowID)
      .pipe(
        finalize(() => {
          this.rootStore.uiStore.setLoader(false);
        }),
      )
      .subscribe({
        next: (data) => {
          this.companyRelationshipManager = data;
        },
        error: (error: AxiosError) => ToastService.axiosError(error),
      });
  };

  getSLAInfo = (companyRowID?: string) => {
    if (!companyRowID) return;

    return CompanyService.getSLAInfo(companyRowID).subscribe({
      next: (data: ISLAInfo) => {
        this.slaInfo = data;
        this.slaInfo.slaExpiryDate = this.slaInfo.slaExpiryDate
          ? DateTimeUtils.dateToLocalDateZone(this.slaInfo.slaExpiryDate)
          : this.slaInfo.slaExpiryDate;
        this.slaInfo.priceReviewDate = this.slaInfo.priceReviewDate
          ? DateTimeUtils.dateToLocalDateZone(this.slaInfo.priceReviewDate)
          : this.slaInfo.priceReviewDate;

        this.slaInfo.slaReminderDate = this.slaInfo.slaReminderDate
          ? DateTimeUtils.dateToLocalDateZone(this.slaInfo.slaReminderDate)
          : this.slaInfo.slaReminderDate;
        this.slaInfo.slaDocuments.forEach(
          (x) =>
            (x.expiryDate = x.expiryDate
              ? DateTimeUtils.dateToLocalDateZone(x.expiryDate)
              : x.expiryDate),
        );
      },
      error: (error: AxiosError) => ToastService.axiosError(error),
    });
  };

  /**
   * Publishes the open client. Callers pass what they already have; a missing id is ignored rather
   * than stored, so a half-loaded screen can never leave a context the app bar would trust.
   */
  setOpenClientContext = (context: IOpenClientContext | null): void => {
    if (!context || !context.companyRowID) return;
    this.openClientContext = context;
  };

  /** Cleared on leaving a client, so a previous client id can never linger (bug 5688). */
  clearOpenClientContext = (): void => {
    this.openClientContext = null;
  };

  resetcompanyOverview = () => {
    this.companyOverview = null;
    // Add New Client deliberately has no overview; that is not a missing client.
    this.companyNotFound = false;
    this.clearOpenClientContext();
  };

  setProspectsCount = (count: number) => {
    this.prospectsCount = count;
  };

  getProspectsCount = () => {
    return CompanyService.getProspectsCount().subscribe({
      next: (count) => {
        this.setProspectsCount(count ?? 0);
      },
      error: (error: AxiosError) => ToastService.axiosError(error),
    });
  };

  getProspects = () => {
    this.prospectsLoading = true;
    return CompanyService.getProspects()
      .pipe(
        finalize(() => {
          this.prospectsLoading = false;
        }),
      )
      .subscribe({
        next: (data) => {
          this.prospects = data || [];
          this.setProspectsCount(this.prospects.length);
        },
        error: (error: AxiosError) => ToastService.axiosError(error),
      });
  };

  private prospectsOnlyMineKey = () =>
    `prospects.onlyMyClients.${
      this.rootStore.authStore?.user?.emailAddress || "anonymous"
    }`;

  loadProspectsOnlyMine = () => {
    const stored = localStorage.getItem(this.prospectsOnlyMineKey());
    this.prospectsOnlyMine = stored == null ? true : stored === "true";
  };

  setProspectsOnlyMine = (value: boolean) => {
    this.prospectsOnlyMine = value;
    // Only ever persisted against a known user: writing under the anonymous key would store the
    // preference where the per-user read can never find it again (bug 5702).
    if (this.rootStore.authStore?.user?.emailAddress) {
      localStorage.setItem(this.prospectsOnlyMineKey(), `${value}`);
    }
  };

  promoteToOnboarding = (companyRowID: string, onSuccess?: () => void) => {
    return CompanyService.promoteToOnboarding(companyRowID).subscribe({
      next: () => {
        this.getProspectsCount();
        this.getCompanyOverview(companyRowID);
        if (onSuccess) onSuccess();
      },
      error: (error: AxiosError) => ToastService.axiosError(error),
    });
  };

  markAsCurrentClient = (
    companyRowID: string,
    onSuccess?: () => void,
    onError?: () => void,
  ) => {
    return CompanyService.markAsCurrentClient(companyRowID).subscribe({
      next: (data) => {
        ToastService.success(data.message);
        this.getProspectsCount();
        this.getCompanyOverview(companyRowID);
        // Not just emptied — put back to page one with no filter, because the caller is about to
        // navigate to Home promising that this client's row is highlighted there (bug 5722).
        this.resetHomeGridState();
        if (onSuccess) onSuccess();
      },
      error: (error: AxiosError) => {
        ToastService.axiosError(error);
        if (onError) onError();
      },
    });
  };
}
